import React, { useMemo, useState } from 'react';
import { Edit2, PowerOff, Eye, RotateCcw, LinkIcon, Copy, RefreshCw, Users, AlertTriangle } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { ConfirmDialog } from '../../../../components/shared/ConfirmDialog';
import { Avatar } from '../../shared/Avatar';
import { formatRelativeTime } from '../../../../utils/formatters';
import { isLearnerAccount, getTeacherClassrooms, getLearnerExistingClassrooms, type Account, type AccountsState } from '../../../../accounts';
import { adminRegenerateLinkCode, adminResetProgress, adminDeleteLearner } from '../../../../data/supabase/adminActions';
import { getMasteryBand } from '../../../shared/masteryBands';
import { GROUP_OPTIONS } from '../../../../config/systemConfig';
import { SupportActionsPanel } from './SupportActionsPanel';
import { ROLE_CONFIG } from './types';
import type { UserRow } from './types';
interface UserDetailPanelProps {
  user: UserRow;
  isEditing: boolean;
  selectedMastery: Array<{
    subjectId: string;
    label: string;
    avg: number;
    count: number;
  }>;
  accounts: AccountsState | undefined;
  store: {
    updateAccountNameById: (id: string, name: string) => void;
    updateAccountEmail: (id: string, email: string) => void;
    updateTeacherDetails: (id: string, details: {
      schoolName: string;
    }) => void;
    updateLearnerDetails: (id: string, details: {
      grade: number;
    }) => void;
    deleteAccount: (id: string) => void;
    resetLearnerProgress: (id: string) => void;
    setLearnerLinkCode: (id: string, code: string) => void;
  };
  onNavigateToLearner?: (learnerId: string) => void;
  onClose: () => void;
  onStartEdit: (user: UserRow) => void;
  onStopEdit: () => void;
  onUserUpdate: (updater: (prev: UserRow | null) => UserRow | null) => void;
}
export function UserDetailPanel({
  user,
  isEditing,
  selectedMastery,
  accounts,
  store,
  onNavigateToLearner,
  onClose,
  onStartEdit,
  onStopEdit,
  onUserUpdate
}: UserDetailPanelProps) {
  const [resetConfirm, setResetConfirm] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  /**
   * Nieuwe koppelcode — van de server, niet uit de browser.
   *
   * De oude versie genereerde lokaal en schreef alleen de store bij. De server
   * hield daardoor de oude code, en de beheerder las een code voor die bij geen
   * enkel profiel hoorde. De ouder kreeg dan "code niet gevonden" en beide
   * partijen dachten dat zíj iets fout deden.
   */
  const handleRegenerateCode = async () => {
    setCodeBusy(true);
    setCodeError(null);
    const res = await adminRegenerateLinkCode(user.id);
    setCodeBusy(false);
    if (!res.ok || !res.code) {
      setCodeError(res.message ?? 'De koppelcode is niet vervangen.');
      return;
    }
    // Pas ná bevestiging van de server de lokale kopie bijwerken. Andersom
    // toont het scherm een code die de database niet kent.
    store.setLearnerLinkCode(user.id, res.code);
    onUserUpdate(prev => {
      if (!prev || !prev.account) return prev;
      return {
        ...prev,
        account: {
          ...prev.account,
          linkCode: res.code
        } as Account
      };
    });
  };

  /**
   * Voortgang resetten — eerst de server, dan pas lokaal.
   *
   * Alleen lokaal resetten hield niet stand: de eerstvolgende synchronisatie
   * zette de oude voortgang gewoon terug. De beheerder zag het lukken en het
   * herstelde zichzelf buiten zijn zicht.
   */
  const handleResetProgress = async () => {
    setActionError(null);
    const res = await adminResetProgress(user.id);
    if (!res.ok) {
      setActionError(res.message ?? 'Resetten is niet gelukt.');
      return;
    }
    store.resetLearnerProgress(user.id);
  };

  /**
   * Verwijderen — eerst de server, dan pas lokaal.
   *
   * Zonder de serverstap verdween het account alleen van dit apparaat, terwijl
   * de bevestiging beloofde dat het definitief was. Een beheerder kon daardoor
   * denken dat hij een verwijderverzoek had afgehandeld.
   */
  const handleDelete = async () => {
    setActionError(null);
    if (user.role === 'learner') {
      const res = await adminDeleteLearner(user.id);
      if (!res.ok) {
        setActionError(res.message ?? 'Verwijderen is niet gelukt.');
        return;
      }
    }
    store.deleteAccount(user.id);
  };

  // Edit form state
  const [editUserName, setEditUserName] = useState(user.name);
  const [editUserEmail, setEditUserEmail] = useState(user.email !== '—' ? user.email : '');
  // Role is intentionally not editable from this panel — kept as a plain
  // derived value rather than useState to avoid the previous "misleading
  // const useState" pattern (audit V10 / P2-2).
  const editUserRole = user.role;
  const [editUserGrade, setEditUserGrade] = useState(user.account && isLearnerAccount(user.account) ? String(user.account.grade || 6) : '6');
  // Grade-mismatch warning when editing learner grade
  const gradeWarnings = useMemo(() => {
    if (editUserRole !== 'learner' || !accounts) return [];
    const newGrade = parseInt(editUserGrade, 10);
    const existing = getLearnerExistingClassrooms(accounts, user.id);
    const warnings: string[] = [];
    for (const {
      classroom,
      teacherName
    } of existing) {
      if (classroom.groupLevel && classroom.groupLevel !== newGrade) {
        warnings.push(`${user.name} zit in "${classroom.name}" (groep ${classroom.groupLevel}) van ${teacherName}. De nieuwe groep is ${newGrade}.`);
      }
    }
    return warnings;
  }, [editUserRole, editUserGrade, accounts, user.id, user.name]);
  const handleEditUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUserName.trim()) return;
    if (editUserName !== user.name) {
      store.updateAccountNameById(user.id, editUserName.trim());
    }
    if (editUserEmail !== (user.email !== '—' ? user.email : '')) {
      store.updateAccountEmail(user.id, editUserEmail.trim());
    }
    // De school wordt hier BEWUST niet meer geschreven. Zie de toelichting bij
    // het schoolblok in het formulier: een vrije naam zou een derde soort school
    // introduceren die noch gekozen noch aangevraagd is, en het ging alleen naar
    // de lokale store.
    if (editUserRole === 'learner' && user.account && isLearnerAccount(user.account)) {
      const newGrade = parseInt(editUserGrade, 10);
      const currentGrade = user.account.grade || 6;
      if (newGrade !== currentGrade) {
        store.updateLearnerDetails(user.id, {
          grade: newGrade
        });
      }
    }
    onUserUpdate(prev => prev ? {
      ...prev,
      name: editUserName.trim(),
      email: editUserEmail.trim() || '—',
      // `organization` blijft staan zoals de server hem kent — dit formulier
      // wijzigt hem niet meer.
      organization: prev.organization
    } : null);
    onStopEdit();
  };
  if (isEditing) {
    // Een server-only rij is niet lokaal te bewerken.
    //
    // Alle schrijfacties hieronder gaan naar de lokale store. Voor een account dat
    // alleen op de server bestaat, vinden die niets om te wijzigen: het formulier
    // opent leeg, "Opslaan" slaagt zonder foutmelding en er verandert niets. Dat
    // is de ergste uitkomst — een beheerder denkt dat hij een naam of groep heeft
    // gecorrigeerd.
    //
    // Bij een leerling telt daar iets bij op dat break glass NIET oplost: de
    // server stuurt naam, e-mail en groep van een leerling helemaal niet mee (0025
    // nulled die kolommen). Er is dus geen veld om te vullen, ook niet met een
    // geopend venster.
    if (user.isServerOnly) {
      return <div className="p-5 rounded-xl border border-slate-200 bg-slate-50" data-id="element-7931" data-mp="3sdsw60i7zq5p">
          <h4 className="flex items-center gap-2 font-bold text-slate-900 mb-1" data-id="element-7932" data-mp="3nidk9e3rw4bx">
            <AlertTriangle className="w-4 h-4 text-slate-500" data-id="element-7933" data-mp="53uqxkwejylh" />
            Dit account is hier niet te bewerken
          </h4>
          <p className="text-sm text-slate-600 mb-3" data-id="element-7934" data-mp="atcfe03y12f">
            {user.name} bestaat op de server, maar niet in de opslag van dit
            apparaat. Naam, e-mailadres en groep worden voor een leerling
            bewust niet meegestuurd — ook niet met een geopend
            toegangsvenster, want dat regelt inzage in de voortgang en niet in
            de persoonsgegevens.
          </p>
          <p className="text-sm text-slate-600" data-id="element-7935" data-mp="k0vav28cqp9e">
            Een naam of groep wijzigen hoort bij wie verantwoordelijke is voor
            dat kind: de ouder in het ouderportaal, of de school. Wat een
            beheerder hier wél kan, staat onder Supportacties: een nieuwe
            koppelcode uitgeven, een ouderkoppeling verbreken, het kind uit alle
            klassen halen en een herstelcode intrekken. Die gaan naar de server
            en vragen een reden, die onder jouw naam wordt vastgelegd.
          </p>
          <div className="pt-4 mt-3 flex justify-end border-t border-slate-200" data-id="element-7936" data-mp="21vvzhg7a7du9">
            <Button type="button" variant="secondary" onClick={onStopEdit} data-id="element-7937" data-mp="26uyxwhsralt9">
              Sluiten
            </Button>
          </div>
        </div>;
    }
    return <div data-id="element-7938" data-mp="1wyvtf5afmb35">
        <form onSubmit={handleEditUser} className="space-y-4" data-id="element-7939" data-mp="1wzovhsyg4cey">
          <Input label="Naam" type="text" required value={editUserName} onChange={e => setEditUserName(e.target.value)} data-id="element-7940" data-mp="1iz4aqr8z06it" />
          
          <Input label="E-mailadres" type="email" value={editUserEmail} onChange={e => setEditUserEmail(e.target.value)} data-id="element-7941" data-mp="1nyz36z8e945z" />
          
          {/*
           GEEN VRIJ SCHOOLVELD MEER.
            Sinds 0041 is de school van een leerkracht óf een verwijzing naar een
           rij in `public.schools` (gekozen, natrekbaar) óf een aanvraagtekst.
           Hier een vrije naam intikken maakte er stil een derde soort van: een
           naam die niemand heeft aangevraagd en die naar geen enkele rij
           verwijst — precies het gegeven waarop verifiëren rust.
            Het veld schreef bovendien alleen naar de lokale store, dus voor een
           server-only leerkracht deed het niets. Twee fouten die elkaar
           maskeerden: hij leek te werken omdat de tekst in het scherm veranderde.
           */}
          {editUserRole === 'teacher' && <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-id="element-7942" data-mp="2nnl7vp8d6e7z">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-7943" data-mp="29saj8tkpqpc2">
                School
              </p>
              <p className="mt-0.5 text-sm font-semibold text-slate-900" data-id="element-7944" data-mp="ybarbz2a2cik">
                {user.organization !== '—' ? user.organization : 'Geen school'}
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-600" data-id="element-7945" data-mp="138186olwpc51">
                De school is hier niet te wijzigen. Een leerkracht kiest zijn
                school uit het register of vraagt hem aan; dat onderscheid is
                waar de verificatie op afgaat. Een aanvraag afhandelen of een
                leerkracht aan een andere school hangen doe je bij{' '}
                <strong data-id="element-7946" data-mp="zspzvbmacybo">Scholen</strong>.
              </p>
            </div>}
          {editUserRole === 'learner' && <>
              {/* Opties uit GROUP_OPTIONS: komt er een groep bij, dan staat hij
                  hier vanzelf. Deze lijst stond hardgecodeerd op 6/7/8 en zou
                  stil achterblijven. */}
              <Select label="Groep" value={editUserGrade} onChange={e => setEditUserGrade(e.target.value)} options={GROUP_OPTIONS} data-id="element-7947" data-mp="lu8ehkjurxwx" />
            
              {gradeWarnings.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1" data-id="element-7948" data-mp="3pfxil0m5g95i">
                  <div className="flex items-center gap-1.5 text-amber-800 text-xs font-semibold" data-id="element-7949" data-mp="3khw1gl8aajwo">
                    <AlertTriangle className="w-3.5 h-3.5" data-id="element-7950" data-mp="1oerbla0tobor" />
                    Groep-mismatch
                  </div>
                  {gradeWarnings.map((w, i) => <p key={i} className="text-xs text-amber-700" data-id="element-7951" data-mp="1y5cbd0s8fm99" data-mpi={i}>
                      {w}
                    </p>)}
                </div>}
            </>}
          <div className="pt-4 flex justify-end gap-2 border-t border-gray-100" data-id="element-7952" data-mp="2265yvt1riz1m">
            <Button type="button" variant="secondary" onClick={onStopEdit} data-id="element-7953" data-mp="15hq6lp79qbkt">
              Annuleren
            </Button>
            <Button type="submit" data-id="element-7954" data-mp="1afrpoa4p2awa">Opslaan</Button>
          </div>
        </form>
      </div>;
  }
  return <div className="space-y-6" data-id="element-7955" data-mp="1efs1ro4bma06">
      {/* Header */}
      <div className="flex items-center gap-4 pb-4 border-b border-gray-100" data-id="element-7956" data-mp="1jgpl4412ha39">
        <Avatar name={user.name} size="md" data-id="element-7957" data-mp="2pw9zdrjnmioe" />
        <div data-id="element-7958" data-mp="2pvgoqfhkr90z">
          <div className="font-semibold text-gray-900" data-id="element-7959" data-mp="2pvqk6ile28e2">{user.name}</div>
          <div className="text-sm text-gray-500" data-id="element-7960" data-mp="2uubi2bd64fzc">{user.organization}</div>
          <div className="text-xs text-gray-400 mt-0.5" data-id="element-7961" data-mp="34z0a0uey4yda">
            Laatste activiteit: {formatRelativeTime(user.lastActive, true)}
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3" data-id="element-7962" data-mp="2v98enxz0w17r">
        <div className="bg-gray-50 rounded-xl p-4" data-id="element-7963" data-mp="2v7xavy5kutgn">
          <div className="text-xs text-gray-500 mb-1" data-id="element-7964" data-mp="2r8epl19uggda">Rol</div>
          <Badge variant={ROLE_CONFIG[user.role].variant} size="sm" data-id="element-7965" data-mp="2r5sh8zf4pwu0">
            {ROLE_CONFIG[user.role].label}
          </Badge>
        </div>
        {user.role === 'learner' && <>
            <div className="bg-emerald-50 rounded-lg p-3" data-id="element-7966" data-mp="2h8e1z34xze3a">
              <div className="text-2xl font-bold text-emerald-700" data-id="element-7967" data-mp="2c9ufn1mfs0ae">
                {user.avgMastery}%
              </div>
              <div className="text-xs text-emerald-600" data-id="element-7968" data-mp="28ac4gxkf33kr">Gem. beheersing</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3" data-id="element-7969" data-mp="ahy59vc08wh7">
              <div className="text-2xl font-bold text-blue-700" data-id="element-7970" data-mp="6hnxcd4njvzp">
                {user.sessionCount}
              </div>
              <div className="text-xs text-blue-600" data-id="element-7971" data-mp="dkkqxhmi6b3y">Sessies</div>
            </div>
            <div className="bg-violet-50 rounded-xl p-4" data-id="element-7972" data-mp="ijoy62pv9yrn">
              <div className="text-xs text-gray-500 mb-1" data-id="element-7973" data-mp="ij5rji5o2wsx">XP Totaal</div>
              <div className="text-xl font-bold text-violet-600" data-id="element-7974" data-mp="4j4j9hn2on3f">
                {user.xp.toLocaleString('nl-NL')}
              </div>
            </div>
          </>}
        {user.role === 'teacher' && <div className="bg-indigo-50 rounded-lg p-3" data-id="element-7975" data-mp="3qypha774px38">
            <div className="text-2xl font-bold text-indigo-700" data-id="element-7976" data-mp="3vqgyhr4eaqgp">
              {user.linkedCount}
            </div>
            <div className="text-xs text-indigo-600" data-id="element-7977" data-mp="3h1ul3uybw8s4">Leerlingen</div>
          </div>}
        {user.role === 'parent' && <div className="bg-rose-50 rounded-lg p-3" data-id="element-7978" data-mp="3m04djxso4va1">
            <div className="text-2xl font-bold text-rose-700" data-id="element-7979" data-mp="3lyl16ia4v4dj">
              {user.linkedCount}
            </div>
            <div className="text-xs text-rose-600" data-id="element-7980" data-mp="3838wbi29jxiw">Kinderen</div>
          </div>}
      </div>

      {/* Teacher Classrooms with JoinCodes */}
      {user.role === 'teacher' && accounts && (() => {
      const classrooms = getTeacherClassrooms(accounts, user.id);
      if (classrooms.length === 0) return null;
      return <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-id="element-7981" data-mp="32em60j1wfjav">
              <h3 className="text-sm font-semibold text-indigo-900 flex items-center gap-1.5 mb-3" data-id="element-7982" data-mp="2ydaaxy09a5va">
                <Users className="w-3.5 h-3.5" data-id="element-7983" data-mp="3cd1x4m0xeskd" /> Klassen & Koppelcodes
              </h3>
              <div className="space-y-2" data-id="element-7984" data-mp="3cap3vcppwvs0">
                {classrooms.map(c => <div key={c.id} className="bg-white rounded-lg px-3 py-2 border border-indigo-200" data-id="element-7985" data-mp="37d5ryjghsv38">
                
                    <div className="flex items-center justify-between" data-id="element-7986" data-mp="3m2200giju83u">
                      <div data-id="element-7987" data-mp="3h5353vr29x4l">
                        <span className="text-sm font-medium text-gray-900" data-id="element-7988" data-mp="3h5v0b5gqxsuc">
                          {c.name}
                        </span>
                        <span className="text-xs text-gray-500 ml-2" data-id="element-7989" data-mp="3q4yim783a47s">
                          {c.learnerIds.length} leerlingen
                        </span>
                      </div>
                      {c.joinCode && <button onClick={() => {
                navigator.clipboard.writeText(c.joinCode || '');
              }} className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-1 rounded border border-indigo-200 hover:bg-indigo-100 transition-colors flex items-center gap-1" title="Klik om koppelcode te kopiëren" data-id="element-7990" data-mp="2spln3ze07n46">
                    
                          <Copy className="w-3 h-3" data-id="element-7991" data-mp="36jw5lyzeql1s" />
                          {c.joinCode}
                        </button>}
                    </div>
                  </div>)}
              </div>
            </div>;
    })()}

      {/* Learner Classroom Membership */}
      {user.role === 'learner' && accounts && (() => {
      const classroomIds = accounts.relationships.learnerToClassrooms?.[user.id] || [];
      const classrooms = classroomIds.map(id => accounts.classrooms?.[id]).filter(Boolean);
      if (classrooms.length === 0) return null;
      return <div className="bg-violet-50 rounded-xl p-4 border border-violet-100" data-id="element-7992" data-mp="1ikspekdmd54e">
              <h3 className="text-sm font-semibold text-violet-900 flex items-center gap-1.5 mb-2" data-id="element-7993" data-mp="1dssksemlto1h">
                <Users className="w-3.5 h-3.5" data-id="element-7994" data-mp="18t68c7elobtq" /> Klas
              </h3>
              <div className="space-y-1.5" data-id="element-7995" data-mp="14scllhcw932f">
                {classrooms.map(c => c && <div key={c.id} className="bg-white rounded-lg px-3 py-2 border border-violet-200 flex items-center justify-between" data-id="element-7996" data-mp="14u74yr8ion4e">
                  
                        <span className="text-sm font-medium text-gray-900" data-id="element-7997" data-mp="1wja7o2kr9pex">
                          {c.name}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-7998" data-mp="1sk1ilb0nufpp">
                          {c.learnerIds.length} klasgenoten
                        </span>
                      </div>)}
              </div>
            </div>;
    })()}

      {/* Link Code for learners */}
      {user.role === 'learner' && user.account && isLearnerAccount(user.account) && <div className="bg-blue-50 rounded-xl p-4 border border-blue-100" data-id="element-7999" data-mp="2c66k2sfwp5es">
            <div className="flex items-center justify-between mb-2" data-id="element-8000" data-mp="2c8as9mku8qkv">
              <h3 className="text-sm font-semibold text-blue-900 flex items-center gap-1.5" data-id="element-8001" data-mp="2g931wgxmgxho">
                <LinkIcon className="w-3.5 h-3.5" data-id="element-8002" data-mp="229blk9jmv50i" /> Koppelcode
              </h3>
              <div className="flex gap-1.5" data-id="element-8003" data-mp="277wkm5n5find">
                {user.account.linkCode && <button onClick={() => {
            navigator.clipboard.writeText(user.account && isLearnerAccount(user.account) ? user.account.linkCode || '' : '');
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
          }} className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-8004" data-mp="2uwo3u7irgqo0">
              
                    <Copy className="w-3 h-3" data-id="element-8005" data-mp="3duieepzjfa8i" />
                    {codeCopied ? 'Gekopieerd!' : 'Kopieer'}
                  </button>}
                <button onClick={handleRegenerateCode} disabled={codeBusy} className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-8006" data-mp="3intn6wnwu132">
              
                  <RefreshCw className={`w-3 h-3 ${codeBusy ? 'animate-spin' : ''}`} data-id="element-8007" data-mp="1fy1tp450i1bq" />
              
                  Nieuwe code
                </button>
              </div>
            </div>
            {codeError && <p role="alert" className="mb-2 text-xs font-semibold text-rose-600" data-id="element-8008" data-mp="o8yrrv0ll329">
          
                {codeError}
              </p>}
            {user.account.linkCode ? <div className="font-mono font-black text-xl text-blue-700 tracking-widest bg-white rounded-lg px-4 py-2 text-center border border-blue-200" data-id="element-8009" data-mp="x9dcot9ngg58">
                {user.account.linkCode}
              </div> : <p className="text-xs text-blue-600" data-id="element-8010" data-mp="28n7ouz5cj3fo">
                Geen koppelcode. Klik "Nieuwe code" om er een te genereren.
              </p>}
          </div>}

      {/* Mastery by subject for learners */}
      {user.role === 'learner' && selectedMastery.length > 0 && <div data-id="element-8011" data-mp="1thd5r6jyjmpz">
          <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-8012" data-mp="1oggug7xa0yaj">
            Beheersing per vak
          </h3>
          <div className="space-y-2.5" data-id="element-8013" data-mp="23e96gq3m74jz">
            {selectedMastery.map(s => <div key={s.subjectId} data-id="element-8014" data-mp="23bd7cyg169ty">
                <div className="flex justify-between text-xs mb-1" data-id="element-8015" data-mp="1yedbthgt4ph3">
                  <span className="text-gray-600" data-id="element-8016" data-mp="2ceedv08t62y5">{s.label}</span>
                  <span className="text-gray-500" data-id="element-8017" data-mp="2cctemxbjtvty">
                    {s.count} skills · {s.avg}%
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-8018" data-mp="2m3xvk2ksbqrf">
                  <div className={`h-2 rounded-full ${getMasteryBand(s.avg).barClass}`} style={{
              width: `${s.avg}%`
            }} data-id="element-8019" data-mp="2m61xiez4ifp2" />
              
                </div>
              </div>)}
          </div>
        </div>}

      {/*
       Supportacties (0042). Staat vóór het blok met Bewerken/Verwijderen omdat
       dit is wat support in de praktijk nodig heeft: een melding van een school
       gaat over een koppeling of een code, niet over een naam.
       */}
      <SupportActionsPanel subjectId={user.id} subjectName={user.name} role={user.role} knownParents={user.role === 'learner' && accounts ? (accounts.relationships.learnerToParents?.[user.id] ?? []).map(id => ({
      id,
      name: accounts.accountsById[id]?.displayName ?? 'Onbekende ouder'
    })) : []} data-id="element-8020" data-mp="3epwa30rias07" />

      {/* Actions */}
      <div className="pt-4 border-t border-gray-100 space-y-2" data-id="element-8021" data-mp="2vrtfm6g6w2v0">
        <div className="flex gap-2" data-id="element-8022" data-mp="2vt4i82xwf4kj">
          <Button size="sm" variant="secondary" className="flex-1" onClick={() => onStartEdit(user)} data-id="element-8023" data-mp="2i0omln7bu8tn">
            
            <Edit2 className="w-3.5 h-3.5 mr-1.5" data-id="element-8024" data-mp="2m1i7s8tdx9iu" /> Bewerken
          </Button>
          <Button size="sm" variant="danger" className="flex-1" onClick={() => setDeleteConfirmOpen(true)} data-id="element-8025" data-mp="2828ylt3ljfod">
            
            <PowerOff className="w-3.5 h-3.5 mr-1.5" data-id="element-8026" data-mp="2d1lfzybp7ujk" /> Verwijderen
          </Button>
        </div>

        {user.role === 'learner' && onNavigateToLearner && <Button size="sm" variant="secondary" className="w-full" onClick={() => {
        onNavigateToLearner(user.id);
        onClose();
      }} data-id="element-8027" data-mp="in7wn5ewwfxk">
          
            <Eye className="w-3.5 h-3.5 mr-1.5" data-id="element-8028" data-mp="9tuvjg6p280e" /> Dashboard
          </Button>}

        {actionError && <p role="alert" className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-xs font-semibold text-rose-700" data-id="element-8029" data-mp="4urqvwumm3sy">
          
            {actionError}
          </p>}

        {user.role === 'learner' && <div className="pt-2 border-t border-gray-100" data-id="element-8030" data-mp="xjfdpwv0x18m">
            {!resetConfirm ? <button onClick={() => setResetConfirm(true)} className="w-full px-4 py-2.5 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors" data-id="element-8031" data-mp="sirptk23d0im">
            
                <RotateCcw className="w-3.5 h-3.5 inline mr-1.5" data-id="element-8032" data-mp="3dajr674qnxba" />
                Voortgang resetten
              </button> : <div className="p-3 bg-red-50 border border-red-200 rounded-xl space-y-2" data-id="element-8033" data-mp="389nn9tke0b5v">
                <p className="text-xs font-semibold text-red-800" data-id="element-8034" data-mp="2y7jufrrivulz">
                  ⚠️ Dit wist de voortgang van deze leerling op de server. Het
                  account en de inlog blijven bestaan. Dit kan niet ongedaan
                  worden gemaakt.
                </p>
                <div className="flex gap-2" data-id="element-8035" data-mp="3cvwrks2cjji2">
                  <button onClick={() => setResetConfirm(false)} className="flex-1 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-8036" data-mp="3cwppd3jbuyo2">
                
                    Annuleren
                  </button>
                  <button onClick={async () => {
              await handleResetProgress();
              setResetConfirm(false);
            }} className="flex-1 py-2 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700" data-id="element-8037" data-mp="3quwffxi1bud6">
                
                    Definitief resetten
                  </button>
                </div>
              </div>}
          </div>}
      </div>

      <ConfirmDialog open={deleteConfirmOpen} title="Account verwijderen" message={user.role === 'learner' ? [`${user.name} en alle bijbehorende gegevens worden van de server verwijderd.`, '', 'Dit is definitief en kan niet ongedaan worden gemaakt. Het kind kan daarna niet meer inloggen.'].join('\n') : [`${user.name} wordt van dit apparaat verwijderd.`, '', 'Let op: voor volwassen accounts verwijdert dit nog niets van de server.'].join('\n')} confirmLabel="Verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={async () => {
      await handleDelete();
      setDeleteConfirmOpen(false);
      onClose();
    }} onCancel={() => setDeleteConfirmOpen(false)} data-id="element-8038" data-mp="3rwtrqwn6xenc" />
      
    </div>;
}